/* eslint-disable react/forbid-prop-types */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import PropTypes from 'prop-types';
import { find, some } from 'lodash';
import { InteractiveTooltip } from './InteractiveTooltip';
import useDebounced from '../../../utilities/UseDebounced';

export default function ClusterTooltip(props) {
    const { renderer, activeItem, itemMarkers, clusterMarkers } = props;

    const map = useMap();
    const { tooltip, tooltipEl } = useTooltip();

    // various tooltip state to determine what/where to show things
    const [tooltipContainerLayer, setTooltipContainerLayer] = useState();
    const hideTooltip = useCallback(() => setTooltipContainerLayer(null), []);
    const hideTooltipDebounced = useDebounced(hideTooltip, 100);

    const showTooltip = useCallback(
        (layer) => {
            setTooltipContainerLayer(layer);
            hideTooltipDebounced.cancel();
        },
        [hideTooltipDebounced]
    );

    // If we hover over a tooltip, cancel any queued hide call and keep showing it. Only
    // when we stop hovering over the tooltip should we hide it
    useEffect(() => {
        tooltip.on('mouseenter', () => hideTooltipDebounced.cancel());
        tooltip.on('mouseleave', () => hideTooltipDebounced());
    }, [hideTooltipDebounced, tooltip]);

    // If the activeItem changes, we need to display or possibly hide a tooltip. This
    // is controlled by hovering over items in the sidebar
    useEffect(() => {
        if (!activeItem) {
            hideTooltipDebounced();
        }

        if (activeItem) {
            hideTooltipDebounced.cancel();
            showTooltip(findActiveItemLayer(itemMarkers, clusterMarkers, activeItem));
        }
    }, [activeItem, clusterMarkers, hideTooltipDebounced, itemMarkers, showTooltip]);

    // Add the hover listeners for the markers
    useEffect(() => {
        itemMarkers.forEach((marker) => {
            marker.on('mouseover', () => showTooltip(marker));
            marker.on('mouseout', () => hideTooltipDebounced());
        });
    }, [hideTooltipDebounced, itemMarkers, showTooltip]);

    // Add listeners for the cluster elements
    useEffect(() => {
        clusterMarkers.forEach((cluster) => {
            cluster.on('mouseover', () => showTooltip(cluster));
            cluster.on('mouseout', () => hideTooltipDebounced());
        });
    }, [clusterMarkers, hideTooltipDebounced, showTooltip]);

    if (!tooltipContainerLayer) {
        ensureTooltipIsHidden(map, tooltip);
        return null;
    }

    // this has to come _after_ our portal call & render. Otherwise, the tooltip will
    // not be aligned properly as Leaflet will align it immediately, prior to React
    // rendering its own content
    setTimeout(() => ensureTooltipIsVisible(map, tooltip, tooltipContainerLayer), 0);

    const items =
        tooltipContainerLayer instanceof L.MarkerCluster
            ? tooltipContainerLayer.getAllChildMarkers().map((marker) => marker.options.item)
            : [tooltipContainerLayer.options.item];

    return createPortal(renderer(items), tooltipEl);
}

function ensureTooltipIsHidden(map, tooltip) {
    if (map.hasLayer(tooltip)) {
        map.removeLayer(tooltip);
    }
}

function ensureTooltipIsVisible(map, tooltip, layer) {
    tooltip.setLatLng(layer.getLatLng());

    if (!map.hasLayer(tooltip)) {
        map.addLayer(tooltip);
    }
}

function useTooltip(initialOptions) {
    const ref = useRef();

    if (!ref.current) {
        const el = document.createElement('div');
        const layer = new InteractiveTooltip({
            direction: 'top',
            ...(initialOptions || {}),
        });

        layer.setContent(el);

        ref.current = { tooltipEl: el, tooltip: layer };
    }

    return ref.current;
}

function findActiveItemLayer(markers, clusters, activeItem) {
    const containingCluster = find(clusters, (cluster) => {
        const childMarkers = cluster.getAllChildMarkers();

        return some(childMarkers, (marker) => marker.options.item === activeItem);
    });

    if (containingCluster) {
        return containingCluster;
    }

    return find(markers, (marker) => marker.options.item === activeItem);
}

ClusterTooltip.propTypes = {
    renderer: PropTypes.func.isRequired,
    activeItem: PropTypes.object,
    itemMarkers: PropTypes.array.isRequired,
    clusterMarkers: PropTypes.array.isRequired,
};

ClusterTooltip.defaultProps = {
    activeItem: null,
};
